import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DialogUploadEditPostContentProps } from "utils/enum/UploadPostContentType";
import { useEffect, useState } from "react";
import Loader2 from "components/app/loader/Loader2";
import useDialogUploadEditPostStore from "store/dialog/useDialogUploadEditPostContentStore";
import { InputDescriptionStaticHeight } from "components/app/flipflop/input-description-static-height/InputDescriptionStaticHeight";
import { DialogViewVideo } from "../dialog-upload-post-content/DialogViewVideo";
import { DialogViewImage } from "../dialog-upload-post-content/DialogViewImage";
import UploadEditSetting from "./components/UploadEditSetting";
import { DialogMusic } from "../dialog-upload-post-content/DialogMusic";

interface DialogProps {
  setIsDialogOpen: (value: boolean) => void;
  dialogUploadEditPostContent: DialogUploadEditPostContentProps;
}

export function DialogUploadEditPostContent({ setIsDialogOpen, dialogUploadEditPostContent }: DialogProps) {
  const { t } = useTranslation();

  const { post, showLoader, textLoader, pageLoading, initializePage, disposePage, onChangeText, setMusic, onPostSubmit } = useDialogUploadEditPostStore();

  const [dialogViewVideo, setDialogViewVideo] = useState<{
    isOpen: boolean;
    videoUrl: string | null;
  }>({
    isOpen: false,
    videoUrl: null,
  });

  const [dialogViewImage, setDialogViewImage] = useState<{
    isOpen: boolean;
    image: string | null;
  }>({
    isOpen: false,
    image: null,
  });

  const [dialogMusic, setDialogMusic] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  const handleDiscardPost = async () => {
    try {
      setIsDialogOpen(false);
    } catch (err) {
      console.error("[error] Trimming failed:", err);
    }
  };

  const handlePostSubmit = async () => {
    try {
      await onPostSubmit((post) => {
        dialogUploadEditPostContent.onSuccess(post);
        setIsDialogOpen(false);
      });
    } catch (err) {
      console.error("[error] Trimming failed:", err);
    }
  };

  useEffect(() => {
    initializePage(dialogUploadEditPostContent.postEdit);

    return () => {
      disposePage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={dialogUploadEditPostContent.isOpen}>
      <DialogContent
        isShowBtnXClose={false}
        className="max-w-full lg:max-w-[700px]  h-[calc(100vh-80px)] py-2 px-0 flex flex-col "
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        {/* {showLoader && <Loader2 text={textLoader} />} */}

        <DialogHeader>
          <DialogTitle className="mt-2 text-center ">{t("Edit Post")}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={() => handleDiscardPost()}
          >
            <X />
          </div>
        </DialogHeader>

        <div className="w-full overflow-x-auto flex-1 items-center" style={{ scrollbarGutter: "stable" }}>
          {post && (
            <div className="px-4 space-y-4">
              {post.media_type === "video" ? (
                <div className="flex flex-row aspect-[16/9] w-full space-x-2 justify-between ">
                  <div className="flex flex-grow flex-col space-y-2 ">
                    <InputDescriptionStaticHeight onChangeText={onChangeText} initialText={post?.media_title} />
                  </div>

                  {post?.post_attachments[0]?.attachment_thumbnail_image_file_src && (
                    <div
                      className="relative aspect-[9/16]  h-full rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                      onClick={() =>
                        setDialogViewVideo({
                          isOpen: true,
                          videoUrl: post.post_attachments?.[0]?.attachment_media_image_file_src,
                        })
                      }
                    >
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                        style={{
                          backgroundImage: `url("${post?.post_attachments[0]?.attachment_thumbnail_image_file_src}")`,
                        }}
                      />

                      <img
                        src={post?.post_attachments[0]?.attachment_thumbnail_image_file_src}
                        alt="Thumbnail"
                        className="relative z-10 w-full h-auto object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              ) : post.media_type === "photo" ? (
                <div className="flex flex-row aspect-[16/9] w-full space-x-2 justify-between ">
                  <div className="flex flex-grow flex-col space-y-2 ">
                    <InputDescriptionStaticHeight onChangeText={onChangeText} initialText={post?.media_title} />
                  </div>

                  <div className="relative aspect-[9/16]  h-full rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                      style={{
                        backgroundImage: `url("${post?.post_attachments[0]?.attachment_thumbnail_image_file_src}")`,
                      }}
                    />

                    <img
                      src={post.post_attachments[0].attachment_media_image_file_src}
                      alt="Thumbnail"
                      className="relative z-10 w-full h-auto object-contain"
                      loading="lazy"
                      onClick={() => setDialogViewImage({ isOpen: true, image: post.post_attachments[0].attachment_media_image_file_src })}
                    />
                  </div>
                </div>
              ) : post.media_type === "album" ? (
                <div className="flex flex-row w-full aspect-[16/9] space-x-2 justify-between">
                  <div className="flex flex-grow flex-col space-y-2 overflow-hidden">
                    <InputDescriptionStaticHeight onChangeText={onChangeText} initialText={post?.media_title} />

                    <div className="w-full overflow-x-auto scroll-smooth">
                      <div className="inline-flex gap-3 px-1 min-w-fit">
                        {post.post_attachments
                          .map((att: any) => att.attachment_media_image_file_src)
                          .map((src: string, i: number) => (
                            <div
                              key={i}
                              className="aspect-square w-28 flex-shrink-0 border rounded-md overflow-hidden"
                              onClick={() => setDialogViewImage({ isOpen: true, image: src })}
                            >
                              <img src={src} alt={`Thumbnail ${i}`} className="w-full h-full object-cover rounded-md" />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>{post.media_type}</>
              )}

              <UploadEditSetting
                onAddMusic={() => {
                  setDialogMusic({
                    isOpen: true,
                  });
                }}
              />
            </div>
          )}
        </div>

        <DialogFooter className="p-4">
          <Button onClick={handlePostSubmit} disabled={pageLoading}>
            {t("Post")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {dialogViewVideo.isOpen && (
        <DialogViewVideo
          setIsDialogOpen={() => {
            setDialogViewVideo({
              isOpen: false,
              videoUrl: null,
            });
          }}
          videoUrl={dialogViewVideo.videoUrl!}
          isOpen={dialogViewVideo.isOpen}
        />
      )}

      {dialogViewImage.isOpen && (
        <DialogViewImage
          setIsDialogOpen={() => {
            setDialogViewImage({
              isOpen: false,
              image: null,
            });
          }}
          image={dialogViewImage.image!}
          isOpen={dialogViewImage.isOpen}
        />
      )}

      {dialogMusic.isOpen && (
        <DialogMusic
          setIsDialogOpen={() => {
            setDialogMusic({
              isOpen: false,
            });
          }}
          isOpen={dialogMusic.isOpen}
          onSelectedMusic={(music) => {
            setDialogMusic({
              isOpen: false,
            });
            setMusic(music);
          }}
        />
      )}
    </Dialog>
  );
}

